import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Shell from "@/components/Shell";
import StatusTimeline from "@/components/StatusTimeline";
import StatusBadge from "@/components/StatusBadge";
import { api, formatBytes, formatDate, formatApiErrorDetail } from "@/lib/api";
import { Upload, FileText, Download, ArrowLeft, Gavel, MessageSquareQuote, Tag } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const DOC_CATEGORIES = [
  { value: "POLICY", label: "Policy Copy" },
  { value: "BILL", label: "Bills / Invoices" },
  { value: "MEDICAL", label: "Medical Records" },
  { value: "KYC", label: "KYC (ID Proof)" },
  { value: "CLAIM_FORM", label: "Claim Form" },
  { value: "OTHER", label: "Other" },
];
const CAT_LABEL = Object.fromEntries(DOC_CATEGORIES.map((c) => [c.value, c.label]));
const CAT_STYLE = {
  POLICY:     "bg-sky-50 text-sky-700",
  BILL:       "bg-amber-50 text-amber-800",
  MEDICAL:    "bg-rose-50 text-rose-700",
  KYC:        "bg-violet-50 text-violet-800",
  CLAIM_FORM: "bg-emerald-50 text-emerald-800",
  OTHER:      "bg-slate-100 text-slate-700",
};

const ALLOWED_EXT = ["pdf", "jpg", "jpeg", "png", "doc", "docx"];
const MAX_MB = 20;

function validateFile(file) {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (!ALLOWED_EXT.includes(ext)) return `${file.name}: file type not allowed (only PDF, JPG, PNG, DOC, DOCX)`;
  if (file.size > MAX_MB * 1024 * 1024) return `${file.name}: file too large (max ${MAX_MB} MB)`;
  return null;
}

export default function ClientCaseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("POLICY");
  const inputRef = useRef(null);

  const loadCase = async () => {
    try {
      const { data } = await api.get(`/cases/${id}`);
      setC(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load case");
      nav("/dashboard");
    }
  };
  const loadDocs = async () => {
    try {
      const { data } = await api.get(`/cases/${id}/documents`);
      setDocs(data || []);
    } catch (_) {}
  };

  useEffect(() => { loadCase(); loadDocs(); }, [id]);

  const handleFiles = async (fileList) => {
    if (!fileList || !fileList.length) return;
    // Client-side validation
    const files = Array.from(fileList);
    const invalid = files.map(validateFile).filter(Boolean);
    if (invalid.length) {
      invalid.forEach((msg) => toast.error(msg));
      return;
    }
    setUploading(true);
    let uploadedCount = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", category);
        await api.post(`/cases/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploadedCount++;
      } catch (e) {
        toast.error(`${file.name}: ${formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"}`);
      }
    }
    setUploading(false);
    if (uploadedCount) toast.success(`${uploadedCount} document(s) uploaded as ${CAT_LABEL[category]}`);
    await loadDocs();
    await loadCase();
  };

  const onDrop = (e) => { e.preventDefault(); handleFiles(Array.from(e.dataTransfer.files || [])); };

  if (!c) return <Shell><div className="px-6 py-10 text-sm text-slate-500">Loading…</div></Shell>;

  return (
    <Shell>
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-10">
        <button data-testid="case-back-btn" onClick={() => nav("/dashboard")} className="text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-emerald-brand font-semibold flex items-center gap-2 mb-6">
          <ArrowLeft className="w-3 h-3" /> All cases
        </button>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-mono text-emerald-brand font-semibold" data-testid="case-detail-case-no">{c.case_no}</div>
            <h1 className="font-heading font-bold text-4xl mt-1 leading-tight" data-testid="case-detail-title">{c.title}</h1>
            {c.category && <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mt-2">{c.category}</div>}
            <p className="mt-3 text-sm text-slate-600 max-w-2xl leading-relaxed">{c.description || "No description provided."}</p>
          </div>
          <StatusBadge status={c.status} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 space-y-6">
            <section className="brand-card p-0 overflow-hidden">
              <div className="border-b border-slate-100 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
                <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Upload documents</span>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Tag className="w-3.5 h-3.5 text-slate-500" />
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Category</span>
                  </div>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="upload-category-select" className="h-9 w-[190px] rounded-full border-slate-200 text-xs font-semibold">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {DOC_CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value} data-testid={`upload-category-${c.value}`}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-slate-500 whitespace-nowrap">{docs.length} on file</span>
                </div>
              </div>
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                data-testid="upload-dropzone"
                className="m-6 border-2 border-dashed border-slate-200 rounded-2xl p-12 text-center cursor-pointer hover:border-emerald-brand hover:bg-emerald-brand/5 transition-colors"
              >
                <div className="w-14 h-14 mx-auto rounded-full bg-emerald-brand/10 text-emerald-brand flex items-center justify-center">
                  <Upload className="w-6 h-6" />
                </div>
                <div className="mt-4 font-heading font-bold text-lg">
                  {uploading ? "Uploading…" : `Drop files or click to upload as ${CAT_LABEL[category]}`}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  PDF · DOC · JPG · PNG — max 20 MB each
                </div>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  data-testid="upload-file-input"
                  className="hidden"
                  onChange={(e) => handleFiles(Array.from(e.target.files || []))}
                />
              </div>

              {docs.length > 0 && (
                <ul className="divide-y divide-slate-100 border-t border-slate-100">
                  {docs.map((d) => (
                    <li key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center justify-between gap-4 px-6 py-3 hover:bg-slate-50">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm font-medium truncate">{d.filename}</span>
                            <span data-testid={`doc-category-${d.id}`} className={`inline-flex shrink-0 items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold ${CAT_STYLE[d.category] || CAT_STYLE.OTHER}`}>
                              {CAT_LABEL[d.category] || "Other"}
                            </span>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">{formatBytes(d.size)} · {formatDate(d.uploaded_at)}</div>
                        </div>
                      </div>
                      <a href={`${api.defaults.baseURL}/documents/${d.id}/download`} target="_blank" rel="noreferrer"
                        data-testid={`doc-download-${d.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 hover:border-navy hover:bg-navy hover:text-white transition-colors text-xs font-semibold">
                        <Download className="w-3 h-3" /> Download
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {(c.opinion || c.client_update) && (
              <section className="brand-card p-0 overflow-hidden">
                <div className="border-b border-slate-100 px-6 py-4">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Updates from your case team</span>
                </div>
                <div className="p-6 space-y-6">
                  {c.opinion && (
                    <div data-testid="case-opinion-block">
                      <div className="inline-flex items-center gap-2 text-emerald-brand text-xs font-semibold uppercase tracking-wider mb-2">
                        <Gavel className="w-4 h-4" /> Expert Opinion
                      </div>
                      <p className="text-[15px] leading-relaxed whitespace-pre-wrap text-slate-700">{c.opinion}</p>
                      {c.recommendation && (
                        <>
                          <div className="text-emerald-brand text-xs font-semibold uppercase tracking-wider mt-5 mb-2">Recommendation</div>
                          <p className="text-[15px] leading-relaxed whitespace-pre-wrap text-slate-700">{c.recommendation}</p>
                        </>
                      )}
                    </div>
                  )}
                  {c.client_update && (
                    <div data-testid="case-client-update-block" className="border-l-2 border-gold pl-4 bg-gold/5 -mx-2 rounded-r-xl p-4">
                      <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-slate-600 font-semibold mb-2">
                        <MessageSquareQuote className="w-4 h-4" /> Latest message
                      </div>
                      <p className="text-[15px] leading-relaxed whitespace-pre-wrap text-slate-700">{c.client_update}</p>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>

          <div className="lg:col-span-4">
            <StatusTimeline status={c.status} timeline={c.timeline} />
          </div>
        </div>
      </div>
    </Shell>
  );
}
